const DB_NAME = 'swa-offline';
const DB_VERSION = 1;

/**
 * WebSQL-compatible rows cursor: result.rs.rows.length / .item(i)
 * @param {unknown} rows
 */
const rowResult = (rows) => {
	const safeRows = Array.isArray(rows) ? rows : [];
	return {
		length: safeRows.length,
		item: (index) => safeRows[index]
	};
};

/**
 * Wrap rows in the WebSQL ResultSet shape expected by db.js.
 * @param {unknown} rows
 */
const resultSet = (rows) => ({ rows: rowResult(rows) });

export default class DBUtil {
	constructor(cb) {
		this.db = null;
		this.ready = false;
		this.initError = null;
		this.pendingQueries = [];

		if (!window.indexedDB) {
			this.initError = new Error('IndexedDB is not available in this browser');
			this.ready = true;
			if (typeof cb === 'function') {
				cb({ error: this.initError, rows: [] });
			}
			return;
		}

		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = (event) => {
			const db = event.target.result;
			if (!db.objectStoreNames.contains('offlineDatasets')) {
				db.createObjectStore('offlineDatasets', { keyPath: 'Id' });
			}
			if (!db.objectStoreNames.contains('offlineDatasetsInfo')) {
				db.createObjectStore('offlineDatasetsInfo', { keyPath: 'Id' });
			}
			if (!db.objectStoreNames.contains('offlineChanges')) {
				const store = db.createObjectStore('offlineChanges', { keyPath: 'rowid', autoIncrement: true });
				store.createIndex('byDatasetTaskType', ['DatasetId', 'TaskId', 'Type'], { unique: false });
			}
		};
		request.onerror = () => {
			this.initError = request.error;
			this.ready = true;
			if (typeof cb === 'function') {
				cb({ error: this.initError, rows: [] });
			}
			this.flushPendingQueries();
		};
		request.onsuccess = () => {
			this.db = request.result;
			this.ready = true;
			if (typeof cb === 'function') {
				cb({ error: false, rows: [] });
			}
			this.flushPendingQueries();
		};
	}

	flushPendingQueries() {
		const pending = this.pendingQueries.splice(0);
		pending.forEach(({ sql, params, cb }) => {
			this.query(sql, params, cb);
		});
	}

	query(sql, params, cb) {
		if (typeof params === 'function') {
			cb = params;
			params = [];
		}
		if (!Array.isArray(params)) {
			params = [];
		}

		if (!this.ready) {
			this.pendingQueries.push({ sql, params, cb });
			return;
		}

		if (this.initError || this.db === null) {
			if (typeof cb === 'function') {
				cb({ query: sql, params, error: this.initError || false, rs: resultSet([]) });
			}
			return;
		}

		this.executeQuery(sql, params, cb);
	}

	executeQuery(sql, params, cb) {
		const finish = (error, rows = []) => {
			const result = {
				query: sql,
				params,
				error: error || false,
				rs: resultSet(rows)
			};
			if (typeof cb === 'function') {
				try {
					cb(result);
				} catch (cbError) {
					console.error('DBUtil callback error:', cbError);
				}
			}
		};

		const normalized = String(sql || '').trim().replace(/\s+/g, ' ');

		try {
			if (/^SELECT \* FROM offlineDatasets$/i.test(normalized)) {
				this.getAll('offlineDatasets').then(rows => finish(false, rows)).catch(err => finish(err, []));
				return;
			}

			if (/^SELECT \* FROM offlineDatasetsInfo WHERE Id = \?$/i.test(normalized)) {
				this.getByKey('offlineDatasetsInfo', params[0]).then(row => finish(false, row ? [row] : [])).catch(err => finish(err, []));
				return;
			}

			if (/^SELECT rowid, \* FROM offlineChanges$/i.test(normalized)) {
				this.getAll('offlineChanges').then(rows => finish(false, rows)).catch(err => finish(err, []));
				return;
			}

			if (/^SELECT rowid, \* FROM offlineChanges WHERE DatasetId = \? AND TaskId = \? AND Type = \?$/i.test(normalized)) {
				this.getChangesByKey(params[0], params[1], params[2]).then(rows => finish(false, rows)).catch(err => finish(err, []));
				return;
			}

			if (/^SELECT \* FROM offlineChanges WHERE DatasetId = \? AND TaskId = \? AND Type = \?$/i.test(normalized)) {
				this.getChangesByKey(params[0], params[1], params[2]).then(rows => finish(false, rows)).catch(err => finish(err, []));
				return;
			}

			if (/^DELETE FROM offlineDatasets WHERE Id = \?$/i.test(normalized)) {
				this.deleteByKey('offlineDatasets', params[0]).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^DELETE FROM offlineDatasetsInfo WHERE Id = \?$/i.test(normalized)) {
				this.deleteByKey('offlineDatasetsInfo', params[0]).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^INSERT INTO offlineDatasets \(Id, JSON\) VALUES \(\?, \?\)$/i.test(normalized)) {
				this.put('offlineDatasets', { Id: params[0], JSON: params[1] }).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^INSERT INTO offlineDatasetsInfo \(Id, JSON\) VALUES \(\?, \?\)$/i.test(normalized)) {
				this.put('offlineDatasetsInfo', { Id: params[0], JSON: params[1] }).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^UPDATE offlineDatasetsInfo SET JSON = \? WHERE Id = \?$/i.test(normalized)) {
				this.put('offlineDatasetsInfo', { Id: params[1], JSON: params[0] }).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^DELETE FROM offlineChanges WHERE rowid = \?$/i.test(normalized)) {
				this.deleteByKey('offlineChanges', params[0]).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^DELETE FROM offlineChanges$/i.test(normalized)) {
				this.clearStore('offlineChanges').then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^UPDATE offlineChanges SET JSON = \? WHERE rowid = \?$/i.test(normalized)) {
				this.updateChange(params[1], { JSON: params[0] }).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^INSERT INTO offlineChanges \(DatasetId, TaskId, Type, JSON\) VALUES \(\?, \?, \?, \?\)$/i.test(normalized)) {
				this.add('offlineChanges', {
					DatasetId: params[0],
					TaskId: params[1],
					Type: params[2],
					JSON: params[3]
				}).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^UPDATE offlineChanges SET JSON = \? WHERE DatasetId = \? AND TaskId = \? AND Type = \?$/i.test(normalized)) {
				this.getChangesByKey(params[1], params[2], params[3]).then(rows => {
					const list = Array.isArray(rows) ? rows : [];
					if (!list.length) {
						return this.add('offlineChanges', {
							DatasetId: params[1],
							TaskId: params[2],
							Type: params[3],
							JSON: params[0]
						});
					}
					return this.updateChange(list[0].rowid, { JSON: params[0] });
				}).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			if (/^INSERT INTO offlineChanges \(JSON, DatasetId, TaskId, Type\) VALUES \(\?, \?, \?, \?\)$/i.test(normalized)) {
				this.add('offlineChanges', {
					JSON: params[0],
					DatasetId: params[1],
					TaskId: params[2],
					Type: params[3]
				}).then(() => finish(false, [])).catch(err => finish(err, []));
				return;
			}

			finish(new Error(`Unsupported offline query: ${sql}`), []);
		} catch (error) {
			finish(error, []);
		}
	}

	runTransaction(storeName, mode, operation) {
		return new Promise((resolve, reject) => {
			try {
				if (!this.db) {
					reject(new Error('IndexedDB is not open'));
					return;
				}
				const transaction = this.db.transaction(storeName, mode);
				const store = transaction.objectStore(storeName);
				operation(store, resolve, reject);
				transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
			} catch (error) {
				reject(error);
			}
		});
	}

	getAll(storeName) {
		return this.runTransaction(storeName, 'readonly', (store, resolve, reject) => {
			const request = store.getAll();
			request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
			request.onerror = () => reject(request.error);
		});
	}

	getByKey(storeName, key) {
		return this.runTransaction(storeName, 'readonly', (store, resolve, reject) => {
			const request = store.get(key);
			request.onsuccess = () => resolve(request.result || null);
			request.onerror = () => reject(request.error);
		});
	}

	put(storeName, value) {
		return this.runTransaction(storeName, 'readwrite', (store, resolve, reject) => {
			const request = store.put(value);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	add(storeName, value) {
		return this.runTransaction(storeName, 'readwrite', (store, resolve, reject) => {
			const request = store.add(value);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	deleteByKey(storeName, key) {
		return this.runTransaction(storeName, 'readwrite', (store, resolve, reject) => {
			const request = store.delete(key);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	clearStore(storeName) {
		return this.runTransaction(storeName, 'readwrite', (store, resolve, reject) => {
			const request = store.clear();
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	updateChange(rowid, updates) {
		return this.getByKey('offlineChanges', rowid).then(existing => {
			if (!existing) {
				throw new Error(`Offline change ${rowid} not found`);
			}
			return this.put('offlineChanges', { ...existing, ...updates, rowid });
		});
	}

	getChangesByKey(datasetId, taskId, type) {
		return this.runTransaction('offlineChanges', 'readonly', (store, resolve, reject) => {
			const index = store.index('byDatasetTaskType');
			const request = index.getAll([datasetId, taskId, type]);
			request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
			request.onerror = () => reject(request.error);
		});
	}
}
